import { PasswordInput } from '@frontend/components/form'
import { Button } from '@nextui-org/react'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

import type { ResetPasswordState } from './lib/type'

import { PASSWORD_REGEX, PASSWORD_REGEX_MESSAGE } from './lib/constraint'
import { useResetPassword } from './lib/hook'

export function ResetPasswordForm() {
  const navigate = useNavigate()

  const { control, handleSubmit, reset, watch } = useForm<ResetPasswordState>({
    defaultValues: {
      confirmPassword: '',
      password: '',
      resetKey: '',
      resetLogin: '',
    },
    mode: 'onTouched',
  })

  const { isLoading, mutate } = useResetPassword()

  const onSubmit = useCallback(
    async (data: ResetPasswordState) => {
      const resetKey: HTMLInputElement = document.querySelector(
        'input[name="resetKey"]',
      )!
      const resetLogin: HTMLInputElement = document.querySelector(
        'input[name="resetLogin"]',
      )!

      if (resetKey && resetLogin) {
        data.resetKey = resetKey.value
        data.resetLogin = resetLogin.value
      }

      await toast.promise(mutate(data), {
        error: (error) => error?.message || 'Failed to change password',
        loading: 'Changing password...',
        success: (response) => {
          reset()
          navigate('/my-account')
          return response?.message || 'Password changed successfully'
        },
      })
    },
    [mutate, navigate, reset],
  )

  const newPassword = watch('password')

  return (
    <>
      <h2 className="mb-2 font-medium uppercase">Change your password</h2>
      <small>Please enter your new password.</small>
      <form
        className="relative mt-6 space-y-6"
        onSubmit={handleSubmit(onSubmit)}
      >
        <PasswordInput
          control={control}
          fieldProps={{
            description:
              'Minimum 8 characters, at least one uppercase letter, one lowercase letter, one number and one symbol.',
            label: 'Password',
            variant: 'bordered',
          }}
          name="password"
          rules={{
            pattern: {
              message: PASSWORD_REGEX_MESSAGE,
              value: PASSWORD_REGEX,
            },
            required: 'Please enter your password',
          }}
        />
        <PasswordInput
          control={control}
          fieldProps={{
            label: 'Confirm Password',
            variant: 'bordered',
          }}
          name="confirmPassword"
          rules={{
            required: 'Please enter your confirm password',
            validate: (value) =>
              value === newPassword || 'Passwords do not match',
          }}
        />

        <Button
          className="font-medium"
          color="primary"
          fullWidth
          isLoading={isLoading}
          size="lg"
          type="submit"
        >
          Change Password
        </Button>
      </form>
    </>
  )
}

export default ResetPasswordForm
